import React, { useState } from "react";
import { Button } from "antd";
import {
    answeredHere,
    answeredLine,
    answerTitle,
    closedLine,
    FixAnswer,
    localTimes,
    PROPOSED_AGAIN_HINT,
    proposedTexts,
    questionAnswers,
    questionNote,
    questionState,
    StreamFix,
} from "./model";
import { answerFix } from "./answer";

interface Props {
    mediaId: string;
    fix: StreamFix;
    /**
     * Заявки полосы (все папки и языки): по ним — открыт ли вопрос по правилу TAG (новая правка ответа идёт или
     * закончилась) и время с итогом новой правки, закрывшей вопрос.
     */
    fixes?: StreamFix[];
    /** Ответ принят — пусть полоса спросит сервер сразу. */
    onAnswered?: () => void;
}

/** Текст правки, который не влез: в папке его нет — он виден здесь, пока вопрос открыт, и у закрытого — с подсказкой. */
const Proposed: React.FC<{ texts: string[]; closed?: boolean }> = ({ texts, closed }) =>
    texts.length ? (
        <div data-testid="fix-question-proposed">
            Ваш текст, который не влез (на ленте его сейчас нет): {texts.map((text) => `«${text}»`).join(", ")}
            {closed ? ` — ${PROPOSED_AGAIN_HINT}` : ""}
        </div>
    ) : null;

/**
 * Вопрос правки переводчику в редакторе: текст вопроса, что сейчас в эфире, текст, который не влез, и кнопки ответа —
 * ровно `question.answers` сервера (вопрос правки времени — «Оставить как в эфире»; «не влезает» — «Сократить свой
 * текст» / «Разрешить сократить соседнюю»). Ход вопроса — как его считает TAG (`questionState`): ответ принят, а новая
 * правка ещё идёт (или короткой правки ещё нет) — вопрос открыт, текст, который не влез, виден; закрытый вопрос —
 * закрытым, с причиной, без кнопок, и текст, который не влез, — с подсказкой, что его можно вписать снова.
 */
export const FixQuestion: React.FC<Props> = ({ mediaId, fix, fixes, onAnswered }) => {
    const [busy, setBusy] = useState<FixAnswer | null>(null);
    const [sent, setSent] = useState<FixAnswer | null>(null);
    const [error, setError] = useState("");

    const all = fixes && fixes.length ? fixes : [fix];
    const phase = questionState(fix, all).phase;
    const proposed = proposedTexts(fix);

    if (phase === "closed") {
        return (
            <div
                data-testid="fix-question-closed"
                data-answer={String(fix.answered?.answer || "")}
                style={{ fontSize: 12, display: "flex", flexDirection: "column", gap: 4, color: "#595959" }}
            >
                <div>{closedLine(fix, all)}</div>
                <Proposed texts={proposed} closed />
            </div>
        );
    }
    if (phase === "none" || !fix.question) return null;

    const question = fix.question;
    const answers = questionAnswers(fix);

    const answer = async (value: FixAnswer) => {
        if (busy) return;
        setBusy(value);
        setError("");
        try {
            await answerFix(mediaId, fix.id, value);
            setSent(value);
            if (onAnswered) onAnswered();
        } catch (err) {
            setError(`Ответ не принят: ${(err as Error).message}`);
        } finally {
            setBusy(null);
        }
    };

    return (
        <div
            data-testid={phase === "answered" ? "fix-question-answered" : "fix-question"}
            data-answer={phase === "answered" ? String(fix.answered?.answer || "") : undefined}
            style={{ fontSize: 12, display: "flex", flexDirection: "column", gap: 4 }}
        >
            {question.text_ru && <div>Вопрос: {localTimes(question.text_ru)}</div>}
            {question.on_air_ru && <div style={{ color: "#8c8c8c" }}>В эфире: {localTimes(question.on_air_ru)}</div>}
            <Proposed texts={proposed} />
            {phase === "answered" ? (
                // Ответ принят, вопрос открыт (TAG держит папку под его правилом): что сейчас — не серым «закрыт».
                <div data-testid="fix-question-wait" style={{ color: "#1677ff" }}>
                    {answeredLine(fix, all)}
                </div>
            ) : sent ? (
                <div data-testid="fix-question-sent">{answeredHere(sent, fix)}</div>
            ) : (
                <>
                    {answers.length > 0 && (
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                            {answers.map((value, index) => (
                                <Button
                                    key={value}
                                    size="small"
                                    type={index === 0 ? "primary" : "default"}
                                    loading={busy === value}
                                    disabled={Boolean(busy) && busy !== value}
                                    onClick={() => void answer(value)}
                                    data-answer={value}
                                >
                                    {answerTitle(value, fix)}
                                </Button>
                            ))}
                        </div>
                    )}
                    <div style={{ color: "#8c8c8c" }}>{questionNote(fix)}</div>
                </>
            )}
            {error && (
                <div data-testid="fix-question-error" style={{ color: "#cf1322" }}>
                    {error}
                </div>
            )}
        </div>
    );
};
